<script setup>
import { ref, computed, nextTick } from 'vue'

import { getI18n } from '@/plugins/i18n'

import { DxPopup } from 'devextreme-vue/popup'
import { DxForm } from 'devextreme-vue/form'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'

import { useLookup, useCurrencyOptions, usePercentageOptions } from '@/composables/data-grid'
import { useProductService } from '@/services/product.service'

import { fixedDecimal } from '@/lib/utils'

import * as dataGridColumn from '@/composables/data-grid-column'

import ChipStatus from '@/components/shared/ChipStatus.vue'
import ProductDropDownBox from '@/components/shared/ProductDropDownBox.vue'

const props = defineProps({
  showDialog: {
    type: Boolean,
    required: true
  },
  data: {
    type: Object,
    default: () => {}
  },
  roundingType: {
    type: Number,
    default: null
  },
  rowData: {
    type: Object,
    default: null
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  saveEndpoint: {
    type: Function,
    default: null
  },
  entityLock: Object,
})
const emit = defineEmits(['onSave', 'onCancel'])

const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()
const portalStore = usePortalStore()
const i18n = getI18n()

const { setTotalVatValue } = useProductService()

const currency = computed(() => webSiteStore.currencies.find(c => c.id === props.data?.currency_ref))

const isReadonly = computed(() => props.readOnly || !props.entityLock.isLocked(false, props.data?.id))
const isExpensePercentage = computed(() => props.roundingType === portalStore.roundingRowType.EXPENSE_PERCENTAGE)
const leftFieldsReadonly = computed(() => props.readOnly || !isExpensePercentage.value)
const amountReadonly = computed(() => isReadonly.value || isExpensePercentage.value)
const roundingLabel = computed(() => isExpensePercentage.value ? i18n.t('common.expenseValue') : i18n.t('common.rounding'))
const roundedTotalLabel = computed(() => isExpensePercentage.value ? i18n.t('common.totalWithExpense') : i18n.t('common.roundedTotal'))

const formRef = ref(null)
const saveButton = ref(null)
const formData = ref({
  document_row_type: webSiteStore.documentRowTypesEnum.EXPENSE,
})

const selectedBrand = computed(() => registryStore.supplierBrands.find(brand => brand.id === formData.value.supplier_brand_ref) ?? null)

const purchaseFamilyId = computed(() => {
  const code = props.roundingType === portalStore.roundingRowType.ROUNDING ? portalStore.hiddenPurchaseFamilyCodes.ROUNDING
    : props.roundingType === portalStore.roundingRowType.EXPENSE_PERCENTAGE ? portalStore.hiddenPurchaseFamilyCodes.EXPENSE_PERCENTAGE : null

  return code ? (portalStore.purchaseFamilies.find(f => f.code === code)?.id ?? null) : null
})

const fetchRoundingProduct = async() => {
  if (purchaseFamilyId.value == null) return null
  const response = await registryStore.api.getProductAsync({ $filter: `purchase_family_id eq ${purchaseFamilyId.value}`, $orderby: 'supplier_brand_ref,code', $top: 1, $count: true })
  return response?.data?.[0] ?? null
}

const initFormData = (product = null) => {
  const documentTotalVat = props.data?.total_vat ?? 0
  const vatTypeRef = props.data?.customer?.vat_type_ref ?? registryStore.companyData.default_vat_type_ref
  const rounding = isExpensePercentage.value ? 0 : fixedDecimal(+(Math.floor(documentTotalVat) - documentTotalVat))

  formData.value = {
    document_row_type: webSiteStore.documentRowTypesEnum.EXPENSE,
    supplier_brand_ref: product ? (product.supplier_brand_ref || registryStore.getDefaultSupplierBrand()?.id || null) : null,
    product_ref: product?.id ?? null,
    description: product?.name ?? null,
    vat_type_ref: vatTypeRef,
    vat_type_percentage: webSiteStore.vatTypes.find(v => v.id === vatTypeRef)?.percentage ?? null,
    document_total_vat: documentTotalVat,
    rounding,
    rounding_percentage: 0,
    rounded_total: fixedDecimal(+(documentTotalVat + rounding)),
  }
}

const initViewData = () => {
  const row = props.rowData ?? {}
  const parentTotalVat = props.data?.total_vat ?? 0
  const rounding = row.total_vat ?? 0
  const documentTotalVat = fixedDecimal(+(parentTotalVat - rounding))

  formData.value = {
    document_row_type: row.document_row_type ?? webSiteStore.documentRowTypesEnum.EXPENSE,
    supplier_brand_ref: row.supplier_brand_ref ?? null,
    product_ref: row.product_ref ?? null,
    description: row.description ?? null,
    vat_type_ref: row.vat_type_ref ?? null,
    vat_type_percentage: row.vat_type_percentage ?? null,
    document_total_vat: documentTotalVat,
    rounding,
    rounding_percentage: isExpensePercentage.value ? (documentTotalVat ? rounding / documentTotalVat : 0) : (row.rounding_percentage ?? 0),
    rounded_total: parentTotalVat,
  }
}

const applyRoundingPercentage = (percentage) => {
  const rounding = fixedDecimal(+(formData.value.document_total_vat * percentage))
  formRef.value?.instance?.updateData({
    rounding_percentage: percentage,
    rounding,
    rounded_total: fixedDecimal(+(formData.value.document_total_vat + rounding)),
  })
}

const applyRounding = (rounding) => {
  formRef.value?.instance?.updateData({
    rounding,
    rounded_total: fixedDecimal(+(formData.value.document_total_vat + rounding)),
    rounding_percentage: formData.value.document_total_vat ? rounding / formData.value.document_total_vat : 0,
  })
}

const applyRoundedTotal = (roundedTotal) => {
  const rounding = fixedDecimal(+(roundedTotal - formData.value.document_total_vat))
  formRef.value?.instance?.updateData({
    rounded_total: roundedTotal,
    rounding,
    rounding_percentage: formData.value.document_total_vat ? rounding / formData.value.document_total_vat : 0,
  })
}

const onProductSelected = (product) => {
  const updates = { product_ref: product?.id ?? null }
  if (product) {
    updates.supplier_brand_ref = product.supplier_brand_ref ?? formData.value.supplier_brand_ref
    updates.description = product.name
  }
  formRef.value?.instance?.updateData(updates)

  if (!product) return

  if (isExpensePercentage.value) {
    applyRoundingPercentage(product.value_percent ?? 0)
    nextTick(() => formRef.value?.instance?.getEditor('rounding_percentage')?.focus())
  } else {
    applyRoundedTotal(fixedDecimal(+Math.floor(formData.value.document_total_vat)))
  }
}

const onShown = () => {
  if (!props.readOnly && !isExpensePercentage.value) saveButton.value?.focus()
}

const onShowing = async() => {
  if (props.readOnly) {
    initViewData()
    return
  }
  props.entityLock.lockEntity()
  const product = isExpensePercentage.value ? null : await fetchRoundingProduct()
  initFormData(product)
}

const onSave = async() => {
  const validation = formRef.value?.instance?.validate()
  if (validation && validation.isValid === false) return
  if (!formData.value.product_ref) return

  const rowData = {
    document_row_type: webSiteStore.documentRowTypesEnum.EXPENSE,
    product_ref: formData.value.product_ref,
    supplier_brand_ref: formData.value.supplier_brand_ref,
    description: formData.value.description,
    vat_type_ref: formData.value.vat_type_ref,
    vat_type_percentage: webSiteStore.vatTypes.find(v => v.id === formData.value.vat_type_ref)?.percentage ?? null,
    quantity: 1,
    discount: 0,
  }
  setTotalVatValue(rowData, formData.value.rounding, rowData)

  await props.saveEndpoint?.(rowData)
  emit('onSave')
}

const currencyOptions = (readOnly = false, onValueChanged = undefined) => ({
  editorType: 'dxNumberBox',
  editorOptions: {
    ...useCurrencyOptions({ currency: currency.value }),
    readOnly,
    onValueChanged,
  },
})

const percentageOptions = (readOnly = false, onValueChanged = undefined) => ({
  editorType: 'dxNumberBox',
  editorOptions: {
    ...usePercentageOptions(),
    min: 0,
    readOnly,
    onValueChanged,
  },
})

const onRoundingPercentageChanged = (e) => { if (e.event) applyRoundingPercentage(e.value) }
const onRoundingChanged = (e) => { if (e.event) applyRounding(e.value) }
const onRoundedTotalChanged = (e) => { if (e.event) applyRoundedTotal(e.value) }

const formItems = computed(() => [
  {
    itemType: 'group',
    colCount: 2,
    items: [
      {
        itemType: 'group',
        colSpan: 1,
        colCount: 12,
        items: [
          {
            label: { text: i18n.t('common.documentRowType') },
            dataField: 'document_row_type',
            colSpan: 6,
            editorType: 'dxSelectBox',
            template: 'documentRowTypeChip',
          },
          { itemType: 'empty', colSpan: 6 },
          {
            dataField: 'supplier_brand_ref',
            label: { text: i18n.t('common.supplierBrand') },
            colSpan: 4,
            editorType: 'dxSelectBox',
            editorOptions: {
              ...useLookup({ dataSource: registryStore.supplierBrands }),
              searchEnabled: true,
              readOnly: leftFieldsReadonly.value,
            },
            validationRules: [{ type: 'required' }],
          },
          ...dataGridColumn.useProductColumns({})
            .filter(column => column.dataField === 'product_ref')
            .map(column => ({ ...column, template: 'productEditor', visible: true, colSpan: 8, label: { text: i18n.t('common.product') } })),
          {
            dataField: 'description',
            colSpan: 12,
            isRequired: true,
            editorOptions: { readOnly: props.readOnly },
          },
          {
            label: { text: i18n.t('common.vat') },
            dataField: 'vat_type_ref',
            editorOptions: {
              ...useLookup({ dataSource: webSiteStore.getLocalizedVatTypes() }),
              readOnly: props.readOnly,
            },
            colSpan: 6,
            editorType: 'dxSelectBox',
            validationRules: [{ type: 'required' }]
          },
        ],
      },
      {
        itemType: 'group',
        colSpan: 1,
        colCount: isExpensePercentage.value ? 2 : 1,
        items: [
          ...(isExpensePercentage.value ? [{
            itemType: 'group',
            colSpan: 1,
            colCount: 1,
            items: [
              {
                dataField: 'rounding_percentage',
                label: { text: i18n.t('common.percentage') },
                ...percentageOptions(isReadonly.value, onRoundingPercentageChanged),
              },
            ],
          }] : []),
          {
            itemType: 'group',
            colSpan: 1,
            colCount: 1,
            items: [
              {
                dataField: 'document_total_vat',
                label: { text: i18n.t('common.documentTotal') },
                ...currencyOptions(true),
                validationRules: [
                  {
                    type: 'custom',
                    reevaluate: true,
                    message: i18n.t('error.validation.must_be_different'),
                    validationCallback: ({ value }) =>
                      fixedDecimal(+value) !== fixedDecimal(+formData.value.rounded_total),
                  },
                ]
              },
              {
                dataField: 'rounding',
                label: { text: roundingLabel.value },
                ...currencyOptions(amountReadonly.value, onRoundingChanged),
              },
              {
                dataField: 'rounded_total',
                label: { text: roundedTotalLabel.value },
                ...currencyOptions(amountReadonly.value, onRoundedTotalChanged),
                validationRules: [
                  {
                    type: 'custom',
                    reevaluate: true,
                    message: i18n.t('error.validation.must_be_different'),
                    validationCallback: ({ value }) =>
                      fixedDecimal(+value) !== fixedDecimal(+formData.value.document_total_vat),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
])

const dialogVisible = computed({
  get: () => props.showDialog,
  set: (value) => { if (!value) emit('onCancel') }
})

const onClose = () => { dialogVisible.value = false }

const popupToolbar = computed(() => [
  ...(props.readOnly ? [] : [{
    widget: 'dxButton',
    toolbar: 'bottom',
    location: 'after',
    options: {
      text: i18n.t('common.save'),
      onClick: onSave,
      onInitialized: (e) => { saveButton.value = e.component },
    },
  }]),
  {
    widget: 'dxButton',
    toolbar: 'bottom',
    location: 'after',
    options: {
      text: props.readOnly ? i18n.t('common.close') : i18n.t('common.cancel'),
      onClick: onClose,
    },
  },
])
</script>

<template>
  <div>
    <dx-popup
      v-model:visible="dialogVisible"
      :drag-enabled="true"
      :resize-enabled="true"
      :width="'80rem'"
      height="auto"
      :show-title="true"
      :title="props.title"
      :toolbar-items="popupToolbar"
      @showing="onShowing"
      @shown="onShown"
      @hidden="() => { if (!props.readOnly) props.entityLock.releaseLock() }"
    >
      <dx-form
        ref="formRef"
        :items="formItems"
        :form-data="formData"
      >
        <template #documentRowTypeChip>
          <div style="margin-top: 0.4rem; margin-bottom: 0.3rem">
            <chip-status :data="webSiteStore.getLocalizedDocumentRowTypes().find(x => x.id === formData.document_row_type)" />
          </div>
        </template>
        <template #productEditor>
          <product-drop-down-box
            :key="formData.product_ref"
            :brand="selectedBrand"
            :purchase-family-id="purchaseFamilyId"
            :document-row-type="formData.document_row_type"
            :disabled="leftFieldsReadonly"
            :data="{ value: formData.product_ref, setValue: onProductSelected }"
            :options="{ hideSupersession: true }"
          />
        </template>
      </dx-form>
    </dx-popup>
  </div>
</template>
